// No direct imports needed - template components import their own dependencies

/**
 * Creates return receipt HTML content
 * @param {Object} orderToProcess - The processed order
 * @param {Object} settings - The receipt settings
 * @param {Function} t - Translation function
 * @param {String} language - Current language
 * @param {Function} formatCurrency - Currency formatter function
 * @returns {String} - HTML content for the return receipt
 */
export const createReturnReceiptHTML = (
  orderToProcess, 
  settings, 
  t, 
  language, 
  formatCurrency
) => {
  const { restaurant, tax, receipts, printing } = settings;
  
  const paperWidth = printing.paper_size || '80mm';
  const paperHeight = printing.paper_height || 'auto';
  const fontSize = printing.font_size || '10';

  // Calculate dimensions with space for RTL text
  const pageWidth = parseInt(paperWidth);
  const contentWidth = pageWidth - 10; // Space for margin
  const pageHeight = parseInt(paperHeight.replace('mm', '')); // Remove 'mm' and parse
  
  // Import receipt templates
  const createReturnHeader = require('../receipt-templates/ReturnHeader').default;
  const createOrderInfo = require('../receipt-templates/OrderInfo').default;
  const createCompanyReceipt = require('../receipt-templates/CompanyReceipt').default;
  const createReturnItems = require('../receipt-templates/ReturnItems').default; 
  const createTotals = require('../receipt-templates/Totals').default;
  const createFooter = require('../receipt-templates/Footer').default;
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <style>
        @page {
          margin: 0;
          size: ${pageWidth}mm ${pageHeight}mm;
        }
        @font-face {
          font-family: 'ArabicPrinterFont';
          src: local('Traditional Arabic'), local('Simplified Arabic'), local('Tahoma');
          unicode-range: U+0600-06FF, U+0750-077F, U+08A0-08FF, U+FB50-FDFF, U+FE70-FEFF; /* Arabic Unicode ranges */
          font-weight: bold;
        }
        /* Special styles for Arabic ranges */
        @font-face {
          font-family: 'ArabicDigits';
          src: local('Traditional Arabic'), local('Simplified Arabic'), local('Courier New'), local('Tahoma');
          unicode-range: U+0660-U+0669; /* Arabic-Indic digits */
          font-weight: bold;
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          text-rendering: geometricPrecision;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        body {
          width: ${pageWidth}mm;
          height: ${pageHeight}mm;
          overflow: hidden;
          margin: 0 auto;
          padding: 0;
          padding-right: 20px;
          display: flex;
          justify-content: center;
        }
        .receipt {
          width: 100%;
          max-width: ${contentWidth}mm;
          padding: 0;
          font-family: monospace, 'ArabicPrinterFont', 'Traditional Arabic', 'Simplified Arabic', Tahoma, Arial, sans-serif;
          font-size: ${fontSize}pt;
          line-height: 1.8; /* Increased even more for better readability */
          direction: ${language === 'ar' ? 'rtl' : 'ltr'};
          text-align: ${language === 'ar' ? 'right' : 'left'};
          margin: 0 auto;
          -webkit-font-smoothing: antialiased;
          unicode-bidi: bidi-override;
          font-weight: bold;
          letter-spacing: normal;
          word-spacing: ${language === 'ar' ? '4px' : 'normal'};
        }
        /* Special styling for Arabic text */
        .arabic-text {
          font-family: monospace !important;
          font-weight: bold;
          letter-spacing: 0;
          word-spacing: 5px;
          font-size: 110%;
        }
        .return-notice {
          text-align: center;
          font-weight: bold;
          font-size: 12px;
          color: #000;
          margin: 5px 0;
          text-decoration: underline;
        }
        table {
          width: 100%;
          max-width: 100%;
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        ${createReturnHeader(restaurant, orderToProcess, t, language)}
        ${createOrderInfo(orderToProcess, t, language)}
        ${createCompanyReceipt(orderToProcess, t)}
        ${createReturnItems(orderToProcess, t, language, formatCurrency)}
        ${createTotals(orderToProcess, tax, t, formatCurrency, language)}
        ${createFooter(receipts, orderToProcess)}
      </div>
    </body>
    </html>
  `;
};
